import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BlockIcon from '@mui/icons-material/Block';
import { useTutorAuth } from '../../context/tutorAuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const statusMeta = {
  pending_review: {
    label: 'Pending Review',
    color: 'warning',
    icon: <HourglassEmptyIcon />,
    helper: 'Your request is waiting for admin review.',
  },
  interview_scheduled: {
    label: 'Interview Scheduled',
    color: 'info',
    icon: <ScheduleIcon />,
    helper: 'An admin has scheduled your interview. Check your email for the date and time.',
  },
  vetted: {
    label: 'Vetted Tutor',
    color: 'success',
    icon: <VerifiedIcon />,
    helper: 'You can now access vetted tutor features.',
  },
  rejected: {
    label: 'Not Approved',
    color: 'error',
    icon: <BlockIcon />,
    helper: 'You can request another review after updating your profile.',
  },
  default: {
    label: 'Not Requested',
    color: 'default',
    icon: <ScheduleIcon />,
    helper: 'Submit your vetting request to get reviewed by admin.',
  },
};

export default function TutorVetting() {
  const { tutor } = useTutorAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/tutors/vetting/status`);
      setStatus(res.data);
    } catch (error) {
      console.error('Failed to load vetting status:', error);
      setSnackbar({ open: true, message: 'Failed to load vetting status', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tutor?.id) {
      fetchStatus();
    }
  }, [tutor?.id]);

  const handleRequestVetting = async () => {
    try {
      setSubmitting(true);
      await axios.post(`${BASE_URL}/api/tutors/vetting/request`);
      setSnackbar({
        open: true,
        message: 'Vetting request submitted successfully.',
        severity: 'success',
      });
      fetchStatus();
    } catch (error) {
      console.error('Failed to submit vetting request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to submit request',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentStatus = status?.tutor_status || 'default';
  const meta = statusMeta[currentStatus] || statusMeta.default;
  const requestStatus = status?.vetting_request?.status;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 2 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>
          Tutor Vetting
        </Typography>
        <Typography color="text.secondary">
          Request review to unlock vetted tutor access for the marketplace.
        </Typography>
      </Stack>

      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {status?.name || tutor?.name}
                  </Typography>
                  <Typography color="text.secondary">{status?.email || tutor?.email}</Typography>
                </Box>

                <Chip
                  icon={meta.icon}
                  label={meta.label}
                  color={meta.color}
                  variant="outlined"
                  sx={{ px: 1.5, py: 2.5, fontWeight: 700 }}
                />
              </Stack>

              <Divider />

              <Box
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  What happens next
                </Typography>
                <Typography color="text.secondary">{meta.helper}</Typography>
                {requestStatus && requestStatus !== currentStatus && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Current request record: {requestStatus.replace(/_/g, ' ')}
                  </Typography>
                )}
                {status?.vetting_request?.status === 'interview_scheduled' && status?.vetting_request?.reviewer_notes && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Interview address/link: {status.vetting_request.reviewer_notes}
                  </Typography>
                )}
                {status?.verified_at && (
                  <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                    Verified on {new Date(status.verified_at).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<ScheduleIcon />}
                  onClick={handleRequestVetting}
                  disabled={submitting || currentStatus === 'pending_review' || currentStatus === 'interview_scheduled' || currentStatus === 'vetted'}
                >
                  {submitting ? 'Submitting...' : currentStatus === 'vetted' ? 'Already Vetted' : 'Request Vetting'}
                </Button>
                <Button variant="outlined" onClick={fetchStatus}>
                  Refresh Status
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
